import { markdownComponentModule } from '../markdown/markdown';
import {
  codemirrorComponentModule,
  trackLineWidgets,
} from '../codemirror/codemirror';
import {
  CODEMIRROR_DEFAULT_OPTIONS,
  codemirrorOptionsComponentModule,
} from '../codemirror/codemirror-options';
import { fullscreenToggleComponentModule } from '../fullscreen-toggle/fullscreen-toggle';
import { syncScrollComponentModule } from '../sync-scroll/sync-scroll';

import markdownEditorTemplate from './markdown-editor.tpl.html';
import markdownEditorNoTabsTemplate from './markdown-editor-no-tabs.tpl.html';
import './markdown-editor.less';
import * as EventUtils from '../../utils/event-utils';
import { appAlertComponentModule } from '../app-alert/app-alert';
import { MarkdownFileUploader } from './markdown-file-uploader';
import { fileUploadServiceModule } from '../../services/file-upload-service/file-upload-service';
import { cloudinaryServiceModule } from '../../services/cloudinary-service/cloudinary-service';
import { languages } from '../../constants/languages';
import { ngFileUploadComponentModule } from '../../vendor/ng-file-upload-component-module';
import { focusNextElement, focusPreviousElement } from '../../utils/dom-utils';
import { docsPaneComponentModule, getDocsURL } from '../docs-pane/docs-pane';

// matches an image ref on it's own line
const MD_BLOCK_IMAGE = /^\s*!\[[^\]]*\]\(([^)]+)\)\s*$/;
const fenceTest = /^(```+)/;
const fenceCount = /^```+/gm;
let markdownEditorIndex = 0;

export const markdownEditorComponentModule = angular
  .module('Q.components.markdown-editor', [
    markdownComponentModule,
    codemirrorComponentModule,
    codemirrorOptionsComponentModule,
    fullscreenToggleComponentModule,
    docsPaneComponentModule,
    syncScrollComponentModule,
    appAlertComponentModule,
    fileUploadServiceModule,
    cloudinaryServiceModule,
    ngFileUploadComponentModule,
  ])

  .directive('markdownEditor', function ($timeout) {
    return {
      restrict: 'AE',
      template: (tElement, tAttrs) => {
        return tAttrs.hideTabs
          ? markdownEditorNoTabsTemplate
          : markdownEditorTemplate;
      },
      require: ['?ngModel', 'markdownEditor'],
      controller: 'MarkdownEditorCtrl as $ctrl',
      bindToController: true,
      scope: {
        disabled: '<ngDisabled',
        readonly: '<ngReadonly',
        nodeId: '@id',
        allowDarkMode: '<',
        hideTabs: '@',
        defaultTab: '@',
        collapseOnEmpty: '<',
        collapseOnBlur: '<',
        autoExpand: '<',
        stretch: '<',
        autofocus: '<',
        disableUploads: '<',
        onCursorActivity: '&',
        onPaste: '&',
        onCopy: '&',
        // Note: for hardcoded multiline placeholders, either use `&#10;` for the newline character or use angular properties:
        //
        //    placeholder="this has&#10;two lines"
        //    placeholder="{{ ::'this has\ntwo lines '}}"
        //
        // Neither are particularly readable, but the top one is probably better.
        placeholder: '@',
        syncScrollId: '@',
      },
      link: function (scope, elem, attrs, [ngModel, ctrl]) {
        ctrl.bindToModel(ngModel);
        elem.attr('tabindex', '-1');

        scope.$watch(
          () => ctrl.hasFocus,
          function (focus) {
            if (focus === true || focus === false) {
              elem.triggerHandler(focus ? 'focus' : 'blur');
            }
          },
        );

        elem.on('focus', ctrl.setFocus);
        elem.on('blur', ctrl.setFocus);

        // prevents animation before initial rendering is complete
        $timeout(() => (ctrl.ready = true), 100);
      },
    };
  })

  .controller(
    'MarkdownEditorCtrl',
    function MarkdownEditorCtrl(
      $scope,
      $element,
      $document,
      codemirrorOptions,
      $timeout,
      appAlert,
      $sce,
      fileUploadService,
      cloudinaryService,
      $window,
    ) {
      const ctrl = this;

      let codeMirror;
      let ngModel;
      const index = ++markdownEditorIndex;

      ctrl.value = '';

      ctrl.syncId = () => {
        return (
          ctrl.syncScrollId || (ctrl.nodeId ? `markdown-editor-${index}` : '')
        );
      };

      ctrl.tabIndexForTabs = () =>
        ((ctrl.collapseOnEmpty && !ctrl.value) || ctrl.collapseOnBlur) &&
        !ctrl.hasFocus
          ? -1
          : 0;

      let blurTimeout = null;
      ctrl.setFocus = function () {
        const focus = $element[0].contains($document[0].activeElement);
        if (blurTimeout) {
          $timeout.cancel(blurTimeout);
          blurTimeout = null;
        }
        if (ctrl.hasFocus !== focus) {
          if (focus) {
            if (ctrl.collapseOnBlur && !ctrl.autoExpand && ctrl.ready) {
              // prevents jarring height transitions
              $element
                .children()
                .css('min-height', $element.offsetHeight() + 'px');
            }
            ctrl.hasFocus = true;
          } else {
            if (ngModel) {
              ngModel.$setViewValue(ctrl.value, 'blur');
            }
            blurTimeout = $timeout(() => {
              if (ctrl.collapseOnBlur && !ctrl.show.edit) {
                ctrl.selectTab('edit', false);
              }
              $element.children().css('min-height', null);
              ctrl.hasFocus = false;
            }, 100);
          }
          $timeout(
            () => {
              if (codeMirror) codeMirror.refresh();
            },
            100,
            false,
          );
        }
      };

      let cmOptions = null;
      ctrl.createOpts = () => {
        if (!cmOptions) {
          // create a new set of options for every markdown editor, because otherwise the placeholder
          // is overridden
          cmOptions = angular.copy(CODEMIRROR_DEFAULT_OPTIONS);
          cmOptions.mode = languages.markdown.codemirror;
          cmOptions.gutters = [];
          cmOptions.lineNumbers = false;
          cmOptions.styleActiveLine = false;
          cmOptions.inputStyle = 'contenteditable';
          cmOptions.spellcheck = true;
          if (!ctrl.allowDarkMode) {
            cmOptions.theme = CODEMIRROR_DEFAULT_OPTIONS.themes.light;
          } else {
            Object.defineProperty(cmOptions, 'theme', {
              get() {
                return CODEMIRROR_DEFAULT_OPTIONS.themes[
                  codemirrorOptions.sharedOpts.theme
                ];
              },
            });
          }
          cmOptions.onLoad = (cm) => {
            codeMirror = cm;
            cm.on('focus', () => {
              $scope.$applyAsync(ctrl.setFocus);
            });
            cm.on('blur', () => {
              $scope.$applyAsync(ctrl.setFocus);
            });
            cm.on('cursorActivity', (cm) => ctrl.onCursorActivity({ cm }));
            let valueInitialized = false;
            cm.on('change', function (evt, change) {
              if (change.origin === 'paste') {
                ctrl.onPaste({ change });
              }
              // During playback (readonly + programmatic setValue), scroll to
              // the end of the new content so the viewer follows along.
              if (change.origin === 'setValue') {
                if (valueInitialized && ctrl.readonly) {
                  const lastLine = cm.lastLine();
                  cm.setCursor({
                    line: lastLine,
                    ch: cm.getLine(lastLine).length,
                  });
                }
                valueInitialized = true;
              }
            });
            const onCopy = () =>
              ctrl.onCopy({ copied: cm.doc.getSelection('\n') });
            cm.on('copy', onCopy);
            cm.on('cut', onCopy);

            if (!ctrl.disableUploads) {
              // Some file upload logic borrowed from https://github.com/chemix/Nette-Texy-CodeMirror
              cm.on('paste', (instance, event) => {
                const clipboardData = event.clipboardData;
                if (angular.isObject(clipboardData)) {
                  _uploadFiles(
                    event,
                    clipboardData.files || clipboardData.items,
                  );
                }
              });
              cm.on('drop', (instance, event) => {
                _uploadFiles(
                  event,
                  event.dataTransfer && event.dataTransfer.files,
                );
              });
            }

            trackLineWidgets(cm, MD_BLOCK_IMAGE, (text, previous) => {
              let image = text.match(MD_BLOCK_IMAGE)[1];
              if (previous) {
                const img = previous.element.firstChild;
                if (img.src !== image) {
                  img.src = image;
                  previous.widget.changed();
                }
                return { keep: true };
              } else {
                const element = document.createElement('div');
                element.className = 'markdown-editor-image-preview';
                const img = document.createElement('img');
                img.src = image;
                element.appendChild(img);
                return {
                  element,
                  options: { above: true, handleMouseEvents: true },
                };
              }
            });
          };
          cmOptions.placeholder = ctrl.placeholder;
          const isInCode = (cm) => {
            let line = cm.getCursor().line;
            // see if we're in a code block
            while (line >= 0) {
              const lineTokens = cm.getLineTokens(line);
              if (lineTokens[0] && lineTokens[0].state.overlay) {
                return !!lineTokens[0].state.overlay.codeBlock;
              }
              line--;
            }
            return false;
          };
          cmOptions.extraKeys.Tab = function (cm) {
            if (isInCode(cm)) {
              return $window.CodeMirror.commands.indentMore(cm);
            }
            focusNextElement();
          };
          cmOptions.extraKeys['Shift-Tab'] = function (cm) {
            if (isInCode(cm)) {
              return $window.CodeMirror.commands.indentLess(cm);
            }
            focusPreviousElement();
          };
          cmOptions.extraKeys.Enter = function (cm) {
            // attempt to complete a fenced code block
            const sel = cm.listSelections();
            const cursor = cm.getCursor();
            if (sel.length === 1 && fenceTest.test(cm.getLine(cursor.line))) {
              const fences = cm.getValue().match(fenceCount);
              if (fences.length % 2) {
                // if there's an odd number of fences, then we'll insert the next one automatically
                const fence = cm.getLine(cursor.line).match(fenceTest)[1];
                cm.replaceSelection('\n');
                const updatedCursor = cm.getCursor();
                cm.replaceSelection(`\n${fence}`);
                cm.setCursor(updatedCursor);
                return;
              }
            }
            // default to the original command
            return $window.CodeMirror.commands.newlineAndIndentContinueMarkdownList(
              cm,
            );
          };
        }
        return cmOptions;
      };

      // bind our internal ngModel value to the external ngModel value
      ctrl.bindToModel = function (_ngModel) {
        ngModel = _ngModel;
        if (!ngModel) return;

        ngModel.$render = () => (ctrl.value = ngModel.$viewValue);
        $scope.$watch('$ctrl.value', (newValue) => {
          ngModel.$setViewValue(newValue, ctrl.hasFocus ? undefined : 'blur');
        });
        $scope.$on('$destroy', () => {
          // ensure we trigger a blur change if getting destroyed
          ngModel.$setViewValue(ctrl.value, 'blur');
        });
      };

      const show = (ctrl.show = {
        edit: true,
        preview: false,
        help: false,
      });

      let smallScreen = false;

      ctrl.selectTab = (which, setFocus = true) => {
        if (which === 'help') {
          if (!ctrl.helpArticle) {
            ctrl.helpArticle = 'reference/markdown';
          }
          if (!ctrl.fullscreen) {
            $window.open(
              getDocsURL(ctrl.helpArticle),
              'qualified-help',
              'noopener,noreferrer',
            );
            return;
          }
        }

        ctrl.setFocus();

        if (!ctrl.fullscreen || smallScreen) {
          show.edit = show.preview = show.help = false;
        } else if (which !== 'edit') {
          show.preview = show.help = false;
        }
        ctrl.show[which] = true;

        if (which === 'edit' && setFocus) {
          if (codeMirror) {
            $timeout(
              () => {
                codeMirror.refresh();
                codeMirror.focus();
              },
              100,
              false,
            );
          }
        }
      };

      ctrl.$onInit = () => {
        $timeout(() => {
          ctrl.selectTab(ctrl.defaultTab || 'edit', false);
        });
      };

      $scope.$watch('$ctrl.fullscreen', (fs, ofs) => {
        if (fs !== ofs) {
          smallScreen = angular.element($window).offsetWidth() <= 768;
          ctrl.ready = false;
          $element.children().css('min-height', null);
          if (fs && !smallScreen) {
            show.edit = show.preview = true;
            show.help = false;
          } else {
            show.edit = true;
            show.preview = show.help = false;
          }
          $timeout(() => {
            ctrl.ready = true;
            if (codeMirror) {
              codeMirror.refresh();
              if (ofs !== undefined) {
                codeMirror.focus();
              }
            }
          }, 100);
        }
      });

      function _uploadFiles(event, files) {
        files = Array.from(files || []);
        if (!files.length || ctrl.readonly || ctrl.disabled) return;

        EventUtils.stopEvent(event);
        if (
          angular.isNumber(event.clientX) &&
          angular.isNumber(event.clientY)
        ) {
          // attempt to move the cursor if we're dropping somewhere specific
          codeMirror.setCursor(
            codeMirror.coordsChar({ left: event.clientX, top: event.clientY }),
          );
        }

        let images = [];
        let rejects = [];
        files.forEach((f) => {
          f = angular.isFunction(f.getAsFile) ? f.getAsFile() : f;
          if (fileUploadService.imageValidator(f)) {
            images.push(f);
          } else {
            rejects.push(f);
          }
        });
        if (!images.length && rejects.length) {
          let msg =
            `<strong>Cannot embed ${rejects.length > 1 ? 'those file types' : 'that file type'}.</strong>` +
            ` You can use ${fileUploadService.imageValidator.label}.`;
          if (event.type === 'paste') {
            msg +=
              '<br>(Note that you may not be able to paste multiple files at once.)';
          }
          appAlert.warning($sce.trustAsHtml(msg));
          return;
        }
        images.forEach((file) => {
          let uploadPromise = fileUploadService
            .handleSingle(file, { showProgress: false, imageExtras: false })
            .then((fileData) => {
              return cloudinaryService.getUrl(fileData, {
                width: 1000,
                height: 1000,
                crop: 'limit',
              });
            })
            .catch(appAlert.modelError);
          new MarkdownFileUploader(codeMirror, file).start(uploadPromise);
        });
      }

      ctrl.selectedFiles = (files) => _uploadFiles({}, files);
    },
  );
